// Frequent Issues Component - Replicating Sentry UI

import { ExternalLink, Bug } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatTimeAgo } from '@/types/performance';
import type { SentryError } from '@/types/performance';

interface FrequentIssuesProps {
  errors: SentryError[];
  isLoading?: boolean;
}

export function FrequentIssues({ errors, isLoading }: FrequentIssuesProps) {
  // Sort by count (most frequent first)
  const sortedErrors = [...errors].sort((a, b) => b.count - a.count).slice(0, 5);

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Frequent Issues</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Bug className="h-4 w-4" />
          Frequent Issues
        </CardTitle>
      </CardHeader>
      <CardContent>
        {sortedErrors.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
            Nenhum erro encontrado 🎉
          </div>
        ) : (
          <div className="space-y-3">
            {sortedErrors.map((error) => (
              <div 
                key={error.id} 
                className="flex items-start justify-between gap-2 py-2 border-b border-border last:border-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`
                      w-2 h-2 rounded-full flex-shrink-0
                      ${error.level === 'error' ? 'bg-red-500' : 
                        error.level === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'}
                    `} />
                    <p className="text-sm font-medium truncate">{error.title}</p>
                  </div>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span>{error.count} eventos</span>
                    <span>•</span>
                    <span>{formatTimeAgo(error.lastSeen)}</span>
                    <span>•</span>
                    <span>
                      {error.operationalStatus === 'ready_to_close'
                        ? 'Pronta para resolver'
                        : (error.sentryStatus || error.status) === 'resolved'
                          ? 'Resolvida'
                          : (error.sentryStatus || error.status) === 'ignored'
                            ? 'Ignorada'
                            : error.operationalStatus === 'monitoring'
                              ? 'Monitorando'
                              : 'Aberta'}
                    </span>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-7 w-7 flex-shrink-0"
                  asChild
                >
                  <a href={error.permalink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
